import * as THREE from "three";
import { Tile, type NpcKind } from "@dogdell/shared";
import {
  DOG_BREEDS,
  DOG_CELL,
  DOG_IDLE_FRAMES,
  DOG_PACK_ROOT,
  DOG_WALK_FRAMES,
  breedForNpc,
  breedFromFur,
  type DogBreedDef,
} from "./DogBreeds";

const KENNEY = "/assets/kenney-rpg";
const DOG_PACK = DOG_PACK_ROOT;

const SPRITE_FILES = {
  tree: "rpgTile200.png",
  treeAlt: "rpgTile179.png",
  bush: "rpgTile176.png",
  mound: "rpgTile177.png",
  fence: "rpgTile202.png",
  portal: "rpgTile220.png",
} as const;

export type SpriteName = keyof typeof SPRITE_FILES | "click" | "spawnPaw" | "waypoint" | NodeSprite;

export type NodeSprite = "digSpot" | "fetchSpot" | "forge" | "bank" | "npc";

/**
 * Canvas + Kenney textures for the 3D park: floor stamps and upright billboards.
 * Dogs use LuizMelo Pet Dogs Pack (CC0) sprite sheets.
 */
export class WorldArt {
  readonly tiles = new Map<Tile, THREE.Texture>();
  readonly sprites = new Map<string, THREE.Texture>();
  private readonly dogs = new Map<string, THREE.Texture>();
  private readonly breedSheets = new Map<
    string,
    { idle: HTMLCanvasElement; walk: HTMLCanvasElement; crop: FrameCrop }
  >();
  private loaded = false;

  async load(): Promise<void> {
    if (this.loaded) {
      return;
    }
    await Promise.all([
      ...Object.values(Tile)
        .filter((value): value is Tile => typeof value === "number")
        .map(async (tile) => {
          this.tiles.set(tile, await this.makeTile(tile));
        }),
      ...Object.entries(SPRITE_FILES).map(async ([name, file]) => {
        this.sprites.set(name, await this.loadSprite(`${KENNEY}/${file}`));
      }),
      this.loadDogPack(),
    ]);
    this.sprites.set("click", canvasTexture(drawPawPrint({ border: true })));
    // Recentered so the print sits in the middle of the black spawn disc.
    this.sprites.set("spawnPaw", canvasTexture(drawPawPrint({ border: true, shiftY: 10 })));
    this.sprites.set("waypoint", canvasTexture(drawBoneFlag()));
    this.sprites.set("digSpot", await this.loadSprite(`${KENNEY}/${SPRITE_FILES.mound}`));
    this.sprites.set("fetchSpot", await this.loadSprite(`${KENNEY}/${SPRITE_FILES.bush}`));
    this.sprites.set("forge", canvasTexture(drawHydrant()));
    this.sprites.set("bank", canvasTexture(drawCushion(0xffd54f)));
    this.sprites.set("rock", canvasTexture(drawRock()));
    this.loaded = true;
  }

  tile(tile: Tile): THREE.Texture {
    return this.tiles.get(tile) ?? this.tiles.get(Tile.Grass)!;
  }

  sprite(name: string): THREE.Texture {
    return this.sprites.get(name) ?? this.sprites.get("bush")!;
  }

  dogSize(fur: number, look?: NpcKind): { width: number; height: number } {
    const breed = look ? breedForNpc(look) : breedFromFur(fur);
    const sheets = this.breedSheets.get(breed.id);
    if (sheets) {
      const aspect = sheets.crop.w / Math.max(1, sheets.crop.h);
      // Height stays breed-scaled; width follows tight sprite aspect after crop.
      return { width: breed.height * aspect, height: breed.height };
    }
    return { width: breed.width, height: breed.height };
  }

  dog(
    fur: number,
    collar: number | null,
    frame = 0,
    look?: NpcKind,
    anim: "auto" | "idle" | "walk" = "auto",
  ): THREE.Texture {
    const breed = look ? breedForNpc(look) : breedFromFur(fur);
    const key = `v14bake-${breed.id}-${collar ?? "none"}-${frame}-${look ?? "pup"}-${anim}`;
    const existing = this.dogs.get(key);
    if (existing) {
      return existing;
    }
    const texture = canvasTexture(
      cutifyDogFrame(
        drawDogFrame(this.breedSheets.get(breed.id), breed, collar, collar !== null, frame, look, anim),
      ),
      true,
    );
    this.dogs.set(key, texture);
    return texture;
  }

  npc(kind: NpcKind): THREE.Texture {
    return this.npcIdle(kind, 0);
  }

  npcIdle(kind: NpcKind, idleFrame: number): THREE.Texture {
    const fur =
      kind === "banker" ? 0xd7ccc8
      : kind === "barksmith" ? 0x6d4c41
      : kind === "shopkeeper" ? 0xc4a574
      : 0x90caf9;
    return this.dog(fur, 0x5d4037, idleFrame, kind, "idle");
  }

  dispose(): void {
    for (const texture of this.tiles.values()) {
      texture.dispose();
    }
    for (const texture of this.sprites.values()) {
      texture.dispose();
    }
    for (const texture of this.dogs.values()) {
      texture.dispose();
    }
    this.tiles.clear();
    this.sprites.clear();
    this.dogs.clear();
    this.breedSheets.clear();
    this.loaded = false;
  }

  private async loadDogPack(): Promise<void> {
    await Promise.all(
      DOG_BREEDS.map(async (breed) => {
        const bundle = await loadBreedSheetBundle(breed);
        this.breedSheets.set(breed.id, bundle);
      }),
    );
  }

  private async makeTile(tile: Tile): Promise<THREE.Texture> {
    // Hand-painted cozy “dog RuneScape” floors - soft grass, dirt paths, warm boards.
    return canvasTexture(paintParkTile(tile), false);
  }

  private async loadSprite(url: string): Promise<THREE.Texture> {
    try {
      const image = await loadImage(url);
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth || 64;
      canvas.height = image.naturalHeight || 64;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return fallbackTexture(0x66bb6a);
      }
      ctx.drawImage(image, 0, 0);
      return canvasTexture(canvas);
    } catch {
      return fallbackTexture(0x66bb6a);
    }
  }
}

export function billboardMaterial(map: THREE.Texture): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    map,
    transparent: true,
    alphaTest: 0.28,
    depthWrite: true,
    // Needed so scale.x flips (walk facing) remain visible.
    side: THREE.DoubleSide,
  });
}

/** Soft exterior stroke so character sprites read against busy grass / paths. */
export const CHAR_RIM_COLOR = 0xfff8e7;
export const CHAR_RIM_HOVER = 0xfff59d;
/** Outline thickness as a fraction of the sprite (UV). Keep tiny - this is not mesh scale. */
export const CHAR_RIM_UV = 0.012;
export const CHAR_RIM_HOVER_UV = 0.016;
/** Interactable billboards - same cream style as pups, slightly thinner. */
export const INTERACT_RIM_UV = 0.009;
export const INTERACT_RIM_HOVER_UV = 0.012;
/** Keep rim just behind the sprite (local -Z after yaw). */
export const CHAR_RIM_DEPTH = 0.03;
/**
 * Billboard center as a fraction of sprite height (plane origin at mid-quad).
 * Under 0.5 plants feet into the ground: camera pitch + sprite padding otherwise
 * make vertical billboards look like they hover above their shadow.
 */
export const SPRITE_FOOT_Y = 0.368;
/** Crouch / beg - lower center so paws still kiss the turf. */
export const SPRITE_FOOT_Y_CROUCH = 0.318;

/**
 * Exterior-only outline from sprite alpha.
 * Samples a thin UV ring around transparent pixels - never a scaled-up fill.
 */
export function silhouetteMaterial(
  map: THREE.Texture,
  color: number = CHAR_RIM_COLOR,
  rimUv: number = CHAR_RIM_UV,
): THREE.ShaderMaterial {
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      map: { value: map },
      color: { value: new THREE.Color(color) },
      alphaTest: { value: 0.4 },
      // Isotropic UV width; clamped so tight crops can't explode into a cream blob.
      dilate: { value: Math.min(0.02, Math.max(0.006, rimUv)) },
    },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D map;
      uniform vec3 color;
      uniform float alphaTest;
      uniform float dilate;
      varying vec2 vUv;

      float sampleA(vec2 uv) {
        return texture2D(map, clamp(uv, 0.0, 1.0)).a;
      }

      float neighbors(float d) {
        float ring = 0.0;
        ring = max(ring, sampleA(vUv + vec2( d,  0.0)));
        ring = max(ring, sampleA(vUv + vec2(-d,  0.0)));
        ring = max(ring, sampleA(vUv + vec2( 0.0,  d)));
        ring = max(ring, sampleA(vUv + vec2( 0.0, -d)));
        ring = max(ring, sampleA(vUv + vec2( d,  d)));
        ring = max(ring, sampleA(vUv + vec2(-d,  d)));
        ring = max(ring, sampleA(vUv + vec2( d, -d)));
        ring = max(ring, sampleA(vUv + vec2(-d, -d)));
        return ring;
      }

      void main() {
        float a = sampleA(vUv);
        if (a > 0.2) discard;
        // Thin band only: ~1× and ~2× dilate (not a filled Minkowski blob).
        float ring = max(neighbors(dilate), neighbors(dilate * 2.0));
        if (ring < alphaTest) discard;
        gl_FragColor = vec4(color, 1.0);
      }
    `,
  });
  mat.userData.rimUv = rimUv;
  return mat;
}

/** Exterior outline plane matched to a character billboard. */
export function makeSilhouette(
  width: number,
  height: number,
  map: THREE.Texture,
  color: number = CHAR_RIM_COLOR,
  rimUv: number = CHAR_RIM_UV,
): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    silhouetteMaterial(map, color, rimUv),
  );
  // Cream rim is baked into dog sprites now; keep mesh for hover recolor hooks only.
  mesh.visible = false;
  mesh.renderOrder = 1;
  mesh.userData.charRim = true;
  return mesh;
}

export function setSilhouetteMap(mesh: THREE.Mesh, map: THREE.Texture): void {
  const mat = mesh.material;
  if (mat instanceof THREE.ShaderMaterial && mat.uniforms.map) {
    mat.uniforms.map.value = map;
  } else if (mat instanceof THREE.MeshBasicMaterial) {
    mat.map = map;
    mat.needsUpdate = true;
  }
}

export function setSilhouetteColor(mesh: THREE.Mesh, color: number): void {
  const mat = mesh.material;
  if (mat instanceof THREE.ShaderMaterial && mat.uniforms.color) {
    (mat.uniforms.color.value as THREE.Color).setHex(color);
  } else if (mat instanceof THREE.MeshBasicMaterial) {
    mat.color.setHex(color);
  }
}

/**
 * Match rim pose to the sprite, then nudge it behind so the exterior ring
 * can't z-fight the dog pixels.
 */
export function placeRimBehind(
  rim: THREE.Object3D,
  sprite: THREE.Object3D,
  scaleX: number,
  scaleY: number,
): void {
  rim.position.copy(sprite.position);
  rim.quaternion.copy(sprite.quaternion);
  rim.scale.set(scaleX, scaleY, 1);
  // Local +Z faces the camera after faceCamera; -Z is behind the sprite.
  rim.translateZ(-CHAR_RIM_DEPTH);
}

/**
 * Screen-aligned billboard - the plane stays parallel to the camera so steep
 * pitch never shows the flat “top” of a yaw-only card.
 */
export function faceCamera(object: THREE.Object3D, camera: THREE.Camera): void {
  object.quaternion.copy(camera.quaternion);
}

/** @deprecated Use {@link faceCamera} - kept as an alias for call sites. */
export function faceCameraYaw(object: THREE.Object3D, camera: THREE.Camera): void {
  faceCamera(object, camera);
}

const _yawBillboardPos = new THREE.Vector3();

/**
 * Yaw-only billboard: keep local up vertical while facing the camera in XZ.
 * For pole-mounted flags so the hoist edge stays flush on the stick.
 */
export function faceCameraHorizontal(object: THREE.Object3D, camera: THREE.Camera): void {
  object.getWorldPosition(_yawBillboardPos);
  const dx = camera.position.x - _yawBillboardPos.x;
  const dz = camera.position.z - _yawBillboardPos.z;
  object.rotation.set(0, Math.atan2(dx, dz), 0);
}

/**
 * Left/right flip so a side-view dog sprite keeps its nose matching world facing
 * as the camera orbits (same rule for local pup, remotes, and NPCs).
 */
export function facingFlipSign(
  worldX: number,
  worldZ: number,
  facing: number,
  camera: THREE.Camera,
): number {
  const toCamX = worldX - camera.position.x;
  const toCamZ = worldZ - camera.position.z;
  // Camera-right in XZ (perpendicular to camera→sprite).
  const rightX = toCamZ;
  const rightZ = -toCamX;
  const walkX = Math.cos(facing);
  const walkZ = Math.sin(facing);
  return walkX * rightX + walkZ * rightZ < 0 ? 1 : -1;
}

function canvasTexture(canvas: HTMLCanvasElement, nearest = false): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = nearest ? THREE.NearestFilter : THREE.LinearFilter;
  texture.minFilter = nearest ? THREE.NearestFilter : THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

function fallbackTexture(color: number): THREE.Texture {
  const canvas = document.createElement("canvas");
  canvas.width = 8;
  canvas.height = 8;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = cssColor(color);
    ctx.fillRect(0, 0, 8, 8);
  }
  return canvasTexture(canvas);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load ${src}`));
    image.src = src;
  });
}

function paintParkTile(tile: Tile): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const g = canvas.getContext("2d")!;
  const seed = (tile + 1) * 97;

  const fillNoise = (base: number, variance: number, softness = 0.55) => {
    const img = g.createImageData(64, 64);
    const data = img.data;
    for (let y = 0; y < 64; y++) {
      for (let x = 0; x < 64; x++) {
        const n =
          Math.sin((x + seed) * 0.19 + y * 0.17) * 0.45 +
          Math.sin(x * 0.07 - (y + seed) * 0.11) * 0.35 +
          Math.sin((x * y + seed) * 0.01) * 0.2;
        const t = (n * 0.5 + 0.5) * softness;
        const c = mixColor(base, variance, t);
        const i = (y * 64 + x) * 4;
        data[i] = (c >> 16) & 0xff;
        data[i + 1] = (c >> 8) & 0xff;
        data[i + 2] = c & 0xff;
        data[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
  };

  switch (tile) {
    case Tile.GrassAlt:
      // Soft meadow twin - nearly the same as Grass so patches blend, not checker.
      fillNoise(0xa6d268, 0x8ebc52, 0.62);
      stipple(g, 0xc8e090, 22, seed);
      break;
    case Tile.Path:
      // Soft poured concrete - transverse joints only (no + grid; ribbons UV V along the walk).
      g.fillStyle = "#c8c0b4";
      g.fillRect(0, 0, 64, 64);
      fillNoise(0xcac1b6, 0xb8afa3, 0.2);
      g.fillStyle = "rgba(255, 252, 245, 0.08)";
      g.fillRect(2, 2, 60, 60);
      // One soft expansion seam across the slab (becomes cross-walk joints on ribbons).
      g.fillStyle = "rgba(90, 78, 64, 0.16)";
      g.fillRect(1, 30, 62, 2);
      g.fillStyle = "rgba(255, 250, 240, 0.1)";
      g.fillRect(1, 29, 62, 1);
      // Fine grit - no longitudinal center line.
      g.fillStyle = "rgba(70, 60, 50, 0.06)";
      for (let i = 0; i < 26; i++) {
        const px = ((seed * 19 + i * 23) % 60) + 2;
        const py = ((seed * 41 + i * 17) % 60) + 2;
        g.fillRect(px, py, 1, 1);
      }
      break;
    case Tile.Pier:
      // Warm dock planks - wood grain, not sidewalk concrete.
      fillNoise(0xb08968, 0x8d6e4c, 0.4);
      g.fillStyle = "rgba(176, 137, 104, 0.85)";
      g.fillRect(1, 1, 62, 62);
      for (let i = 0; i < 5; i++) {
        const py = 3 + i * 12;
        g.fillStyle = i % 2 === 0 ? "rgba(141, 110, 76, 0.55)" : "rgba(188, 152, 112, 0.45)";
        g.fillRect(2, py, 60, 10);
        g.fillStyle = "rgba(74, 52, 32, 0.45)";
        g.fillRect(2, py + 10, 60, 2);
        // Nail heads
        g.fillStyle = "rgba(55, 40, 28, 0.55)";
        g.fillRect(8, py + 3, 2, 2);
        g.fillRect(54, py + 3, 2, 2);
      }
      g.strokeStyle = "rgba(90, 60, 35, 0.35)";
      g.lineWidth = 2;
      g.strokeRect(1, 1, 62, 62);
      break;
    case Tile.Sand:
      // Honey playground sand.
      fillNoise(0xf0d090, 0xe0bc70, 0.5);
      g.fillStyle = "rgba(210, 170, 100, 0.16)";
      for (let i = 0; i < 18; i++) {
        const px = ((seed * 17 + i * 29) % 56) + 4;
        const py = ((seed * 31 + i * 13) % 56) + 4;
        g.fillRect(px, py, 2, 2);
      }
      break;
    case Tile.Floor:
      // Warm plaza / court pavers.
      fillNoise(0xe8c49a, 0xd4a878, 0.48);
      g.strokeStyle = "rgba(160, 120, 70, 0.14)";
      g.lineWidth = 1.2;
      for (let i = 0; i < 4; i++) {
        g.beginPath();
        g.moveTo(6 + i * 14, 4);
        g.lineTo(6 + i * 14, 60);
        g.stroke();
        g.beginPath();
        g.moveTo(4, 8 + i * 14);
        g.lineTo(60, 8 + i * 14);
        g.stroke();
      }
      break;
    case Tile.CourtBasket:
      // Outdoor basketball asphalt - cool charcoal, grit, not plaza tan.
      g.fillStyle = "#5c6168";
      g.fillRect(0, 0, 64, 64);
      fillNoise(0x646970, 0x4e545c, 0.22);
      g.fillStyle = "rgba(255, 255, 255, 0.04)";
      for (let i = 0; i < 34; i++) {
        const px = ((seed * 17 + i * 29) % 60) + 2;
        const py = ((seed * 43 + i * 19) % 60) + 2;
        g.fillRect(px, py, 1, 1);
      }
      g.fillStyle = "rgba(20, 22, 28, 0.12)";
      for (let i = 0; i < 22; i++) {
        const px = ((seed * 13 + i * 37) % 58) + 3;
        const py = ((seed * 31 + i * 11) % 58) + 3;
        g.fillRect(px, py, 1 + (i % 2), 1);
      }
      break;
    case Tile.CourtTennis:
      // Hard tennis court - classic park green asphalt.
      g.fillStyle = "#3d8a5a";
      g.fillRect(0, 0, 64, 64);
      fillNoise(0x449464, 0x347850, 0.2);
      g.fillStyle = "rgba(255, 255, 240, 0.05)";
      for (let i = 0; i < 26; i++) {
        const px = ((seed * 23 + i * 31) % 60) + 2;
        const py = ((seed * 37 + i * 17) % 60) + 2;
        g.fillRect(px, py, 1, 1);
      }
      g.fillStyle = "rgba(20, 50, 30, 0.1)";
      for (let i = 0; i < 18; i++) {
        const px = ((seed * 7 + i * 41) % 58) + 3;
        const py = ((seed * 19 + i * 13) % 58) + 3;
        g.fillRect(px, py, 2, 1);
      }
      break;
    case Tile.CourtLine:
      // Painted white court lines.
      g.fillStyle = "#f4f2ea";
      g.fillRect(0, 0, 64, 64);
      fillNoise(0xf7f5ee, 0xe8e4d8, 0.12);
      g.fillStyle = "rgba(200, 195, 180, 0.18)";
      for (let i = 0; i < 12; i++) {
        const px = ((seed * 11 + i * 27) % 58) + 3;
        const py = ((seed * 29 + i * 15) % 58) + 3;
        g.fillRect(px, py, 1, 1);
      }
      break;
    case Tile.Water:
      // Quiet pond - soft teal.
      fillNoise(0x68b8c8, 0x52a4b8, 0.45);
      g.fillStyle = "rgba(255, 250, 235, 0.3)";
      g.beginPath();
      g.ellipse(22, 28, 14, 5, -0.3, 0, Math.PI * 2);
      g.fill();
      g.beginPath();
      g.ellipse(44, 42, 10, 4, 0.2, 0, Math.PI * 2);
      g.fill();
      break;
    case Tile.Flowers:
      fillNoise(0xa4d068, 0x88b850);
      bloom(g, 14, 20, "#ff9ec8");
      bloom(g, 40, 34, "#fff59d");
      bloom(g, 28, 12, "#e8b4ff");
      bloom(g, 48, 16, "#ffcbb0");
      bloom(g, 22, 44, "#ffb3d9");
      bloom(g, 50, 48, "#fff1a8");
      bloom(g, 36, 50, "#b8f0e8");
      break;
    case Tile.Rock:
      fillNoise(0xd8cec6, 0xc0b6ae, 0.45);
      break;
    default:
      // Soft sunlit meadow - warm nostalgic green with gentle noise (no hard tiles).
      fillNoise(0xaed86e, 0x94c458, 0.62);
      stipple(g, 0xd4e898, 24, seed + 3);
      break;
  }

  // Feather edges so neighboring tile materials don't sparkle at seams.
  const edge = g.createLinearGradient(0, 0, 64, 0);
  edge.addColorStop(0, "rgba(0,0,0,0.04)");
  edge.addColorStop(0.5, "rgba(0,0,0,0)");
  edge.addColorStop(1, "rgba(0,0,0,0.04)");
  g.fillStyle = edge;
  g.fillRect(0, 0, 64, 64);
  return canvas;
}

function mixColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

function stipple(g: CanvasRenderingContext2D, color: number, count: number, seed: number): void {
  g.fillStyle = cssColor(color);
  for (let i = 0; i < count; i++) {
    const x = ((seed * (i + 3) * 17) % 58) + 3;
    const y = ((seed * (i + 5) * 29) % 58) + 3;
    g.globalAlpha = 0.22;
    g.beginPath();
    g.ellipse(x, y, 2.2, 1.4, (i % 5) * 0.4, 0, Math.PI * 2);
    g.fill();
  }
  g.globalAlpha = 1;
}

function bloom(ctx: CanvasRenderingContext2D, x: number, y: number, petal: string): void {
  ctx.fillStyle = petal;
  ctx.beginPath();
  ctx.arc(x - 3, y, 3, 0, Math.PI * 2);
  ctx.arc(x + 3, y, 3, 0, Math.PI * 2);
  ctx.arc(x, y - 3, 3, 0, Math.PI * 2);
  ctx.arc(x, y + 3, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#fff6c8";
  ctx.beginPath();
  ctx.arc(x, y, 2, 0, Math.PI * 2);
  ctx.fill();
}

function drawPawPrint(opts?: { border?: boolean; shiftY?: number }): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const g = canvas.getContext("2d")!;
  const border = opts?.border !== false;
  const dy = opts?.shiftY ?? 0;

  const pads: [number, number, number, number][] = [
    [64, 78 + dy, 28, 24],
    [34, 42 + dy, 16, 18],
    [54, 28 + dy, 15, 17],
    [76, 28 + dy, 15, 17],
    [94, 42 + dy, 16, 18],
  ];

  g.fillStyle = "rgba(0,0,0,0.35)";
  for (const [x, y, rx, ry] of pads) {
    ellipse(g, x + 2, y + 3, rx, ry);
  }

  if (border) {
    g.strokeStyle = "#1a1208";
    g.lineWidth = 6;
    for (const [x, y, rx, ry] of pads) {
      g.beginPath();
      g.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
      g.stroke();
    }
  }

  g.fillStyle = "#ffe566";
  for (const [x, y, rx, ry] of pads) {
    const inset = border ? 1.5 : 0;
    ellipse(g, x, y, rx - inset, ry - inset);
  }

  g.fillStyle = "#fff8c8";
  ellipse(g, 56, 70 + dy, 8, 6);
  ellipse(g, 30, 36 + dy, 4, 4);
  ellipse(g, 50, 24 + dy, 3.5, 3.5);
  ellipse(g, 72, 24 + dy, 3.5, 3.5);
  ellipse(g, 90, 36 + dy, 4, 4);

  return canvas;
}

function drawBoneFlag(): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = 96;
  canvas.height = 64;
  const g = canvas.getContext("2d")!;

  // Straight hoist edge against the pole; round only the free (right) side.
  g.fillStyle = "#1a1208";
  g.beginPath();
  g.moveTo(0, 4);
  g.lineTo(86, 4);
  g.quadraticCurveTo(94, 4, 94, 12);
  g.lineTo(94, 52);
  g.quadraticCurveTo(94, 60, 86, 60);
  g.lineTo(0, 60);
  g.closePath();
  g.fill();

  // Leaf green cloth - reads as “go here,” not a white retreat flag.
  g.fillStyle = "#3f8f3a";
  g.beginPath();
  g.moveTo(2, 6);
  g.lineTo(85, 6);
  g.quadraticCurveTo(91, 6, 91, 12);
  g.lineTo(91, 52);
  g.quadraticCurveTo(91, 58, 85, 58);
  g.lineTo(2, 58);
  g.closePath();
  g.fill();

  // Soft highlight so the wave catches light.
  g.fillStyle = "rgba(255, 248, 220, 0.18)";
  g.beginPath();
  g.moveTo(8, 8);
  g.lineTo(48, 8);
  g.quadraticCurveTo(40, 28, 52, 52);
  g.lineTo(8, 52);
  g.closePath();
  g.fill();

  // Hoist sleeve (square strip flush with the pole)
  g.fillStyle = "#3e2a14";
  g.fillRect(0, 4, 7, 56);
  g.fillStyle = "#6b4a28";
  g.fillRect(2, 6, 3, 52);
  g.fillStyle = "#1a1208";
  g.fillRect(6, 4, 1.5, 56);

  // Cream paw on green (matches park destination mark, stays readable).
  const cx = 50;
  const cy = 33;
  const pads: [number, number, number, number][] = [
    [cx, cy + 5.5, 9.5, 8],
    [cx - 11, cy - 5.5, 5.2, 5.8],
    [cx - 4, cy - 10, 4.8, 5.4],
    [cx + 4, cy - 10, 4.8, 5.4],
    [cx + 11, cy - 5.5, 5.2, 5.8],
  ];
  g.fillStyle = "#1a1208";
  for (const [x, y, rx, ry] of pads) {
    ellipse(g, x, y, rx + 0.8, ry + 0.8);
  }
  g.fillStyle = "#ffe566";
  for (const [x, y, rx, ry] of pads) {
    ellipse(g, x, y, rx, ry);
  }
  g.fillStyle = "#fff8c8";
  ellipse(g, cx - 2, cy + 3.5, 4.2, 3.2);
  ellipse(g, cx - 11, cy - 6.2, 2.2, 2.2);
  ellipse(g, cx - 4, cy - 10.5, 2, 2);
  ellipse(g, cx + 4, cy - 10.5, 2, 2);
  ellipse(g, cx + 11, cy - 6.2, 2.2, 2.2);

  return canvas;
}

function drawHydrant(): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 80;
  const g = canvas.getContext("2d")!;
  g.fillStyle = "rgba(0,0,0,0.22)";
  ellipse(g, 32, 74, 14, 4);
  g.fillStyle = "#ff8a3d";
  roundRect(g, 26, 22, 12, 42, 4);
  roundRect(g, 18, 34, 28, 10, 4);
  g.fillStyle = "#fff8ee";
  roundRect(g, 28, 16, 8, 8, 2);
  g.fillStyle = "#c62828";
  ellipse(g, 32, 20, 5, 3);
  return canvas;
}

function drawCushion(color: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const g = canvas.getContext("2d")!;
  g.fillStyle = "rgba(0,0,0,0.2)";
  ellipse(g, 32, 52, 18, 5);
  g.fillStyle = cssColor(darken(color, 0.18));
  roundRect(g, 10, 28, 44, 22, 10);
  g.fillStyle = cssColor(color);
  roundRect(g, 12, 24, 40, 20, 10);
  g.fillStyle = "rgba(255,248,238,0.85)";
  ellipse(g, 32, 34, 12, 6);
  return canvas;
}

function drawRock(): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 56;
  const g = canvas.getContext("2d")!;
  g.fillStyle = "rgba(0,0,0,0.25)";
  ellipse(g, 32, 50, 20, 5);
  g.fillStyle = "#8d8174";
  ellipse(g, 32, 36, 20, 14);
  g.fillStyle = "#a89a8c";
  ellipse(g, 28, 32, 12, 9);
  g.fillStyle = "#6e655c";
  ellipse(g, 40, 38, 8, 6);
  g.fillStyle = "#c4b8aa";
  ellipse(g, 24, 30, 5, 3);
  return canvas;
}

interface FrameCrop {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type BreedSheetBundle = {
  idle: HTMLCanvasElement;
  walk: HTMLCanvasElement;
  crop: FrameCrop;
};

const breedSheetCache = new Map<string, BreedSheetBundle>();

/** Shared sheet loader for park WorldArt + login breed carousel. */
export async function loadBreedSheetBundle(breed: DogBreedDef): Promise<BreedSheetBundle> {
  const hit = breedSheetCache.get(breed.id);
  if (hit) {
    return hit;
  }
  const base = `${DOG_PACK}/${breed.folder}`;
  const [idle, walk] = await Promise.all([
    loadSheetCanvas(`${base}/${breed.idleFile}`),
    loadSheetCanvas(`${base}/${breed.walkFile}`),
  ]);
  const crop = measureBreedCrop(idle, DOG_IDLE_FRAMES, walk, DOG_WALK_FRAMES);
  const bundle = { idle, walk, crop };
  breedSheetCache.set(breed.id, bundle);
  return bundle;
}

/** Same cutify + cream outline bake the park billboards use. */
export function renderInGameDogCanvas(
  breed: DogBreedDef,
  sheets: BreedSheetBundle,
  frame: number,
  anim: "idle" | "walk" = "walk",
): HTMLCanvasElement {
  return cutifyDogFrame(
    drawDogFrame(sheets, breed, null, false, frame, undefined, anim),
  );
}

async function loadSheetCanvas(src: string): Promise<HTMLCanvasElement> {
  const image = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(image, 0, 0);
  knockOutNearBlack(ctx, canvas.width, canvas.height);
  return canvas;
}

/** Pack sheets use solid black mattes - convert to alpha. */
function knockOutNearBlack(ctx: CanvasRenderingContext2D, w: number, h: number, threshold = 14): void {
  const image = ctx.getImageData(0, 0, w, h);
  const data = image.data;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i]! <= threshold && data[i + 1]! <= threshold && data[i + 2]! <= threshold) {
      data[i + 3] = 0;
    }
  }
  ctx.putImageData(image, 0, 0);
}

function measureBreedCrop(
  idle: HTMLCanvasElement,
  idleFrames: number,
  walk: HTMLCanvasElement,
  walkFrames: number,
): FrameCrop {
  const pad = 2;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const scan = (sheet: HTMLCanvasElement, frames: number) => {
    const cellW = Math.floor(sheet.width / frames) || DOG_CELL;
    const cellH = sheet.height || DOG_CELL;
    const ctx = sheet.getContext("2d")!;
    for (let f = 0; f < frames; f++) {
      const data = ctx.getImageData(f * cellW, 0, cellW, cellH).data;
      for (let y = 0; y < cellH; y++) {
        for (let x = 0; x < cellW; x++) {
          if (data[(y * cellW + x) * 4 + 3]! > 20) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }
    }
  };
  scan(idle, idleFrames);
  scan(walk, walkFrames);
  if (!Number.isFinite(minX)) {
    return { x: 0, y: 0, w: DOG_CELL, h: DOG_CELL };
  }
  return {
    x: Math.max(0, minX - pad),
    y: Math.max(0, minY - pad),
    w: Math.max(1, maxX - minX + 1 + pad * 2),
    h: Math.max(1, maxY - minY + 1 + pad * 2),
  };
}

function drawDogFrame(
  sheets: { idle: HTMLCanvasElement; walk: HTMLCanvasElement; crop: FrameCrop } | undefined,
  breed: DogBreedDef,
  collar: number | null,
  wearingCollar: boolean,
  frame: number,
  look?: NpcKind,
  anim: "auto" | "idle" | "walk" = "auto",
): HTMLCanvasElement {
  const crop = sheets?.crop ?? { x: 0, y: 0, w: DOG_CELL, h: DOG_CELL };
  const canvas = document.createElement("canvas");
  canvas.width = crop.w;
  canvas.height = crop.h;
  const g = canvas.getContext("2d")!;

  if (sheets) {
    const mode = anim === "auto" ? (frame > 0 ? "walk" : "idle") : anim;
    const sheet = mode === "walk" ? sheets.walk : sheets.idle;
    const count = mode === "walk" ? DOG_WALK_FRAMES : DOG_IDLE_FRAMES;
    const index =
      mode === "walk"
        ? (Math.max(0, frame - (anim === "auto" ? 1 : 0)) % count)
        : Math.max(0, frame) % count;
    const cellW = Math.floor(sheet.width / count) || DOG_CELL;
    g.imageSmoothingEnabled = false;
    g.drawImage(
      sheet,
      index * cellW + crop.x,
      crop.y,
      crop.w,
      crop.h,
      0,
      0,
      crop.w,
      crop.h,
    );
  } else {
    drawFallbackDog(g, breed);
  }

  // Accessories in crop-relative space (sprites face right; neck ~ mid-upper).
  const nx = crop.w * 0.62;
  const ny = crop.h * 0.42;
  if (wearingCollar || look) {
    const color = look === "banker" ? 0xc9a227 : look === "barksmith" ? 0xc62828 : collar ?? 0x5d4037;
    g.fillStyle = cssColor(color);
    g.fillRect(nx - 8, ny, crop.w * 0.28, Math.max(3, crop.h * 0.08));
    g.fillStyle = look === "shopkeeper" ? "#fff176" : "#ffd54f";
    g.beginPath();
    g.arc(nx + 4, ny + crop.h * 0.1, Math.max(2, crop.w * 0.05), 0, Math.PI * 2);
    g.fill();
  }
  if (look === "shopkeeper") {
    g.fillStyle = "#66bb6a";
    g.fillRect(crop.w * 0.25, crop.h * 0.2, crop.w * 0.35, crop.h * 0.12);
  }
  if (look === "barksmith") {
    g.fillStyle = "#c62828";
    g.fillRect(nx - 10, ny - 4, crop.w * 0.32, Math.max(3, crop.h * 0.06));
  }
  if (look === "guide") {
    g.fillStyle = "#42a5f5";
    g.fillRect(nx - 10, ny - 2, crop.w * 0.3, Math.max(3, crop.h * 0.06));
  }
  if (look === "banker") {
    g.fillStyle = "#c9a227";
    roundRect(g, crop.w * 0.28, crop.h * 0.08, crop.w * 0.4, Math.max(4, crop.h * 0.1), 2);
  }

  return canvas;
}

/** Soft chibi polish - keep source aspect; overlays stay on opaque pixels only. */
function cutifyDogFrame(source: HTMLCanvasElement): HTMLCanvasElement {
  const pad = 3;
  const out = document.createElement("canvas");
  out.width = source.width + pad * 2;
  out.height = source.height + pad * 2;
  const g = out.getContext("2d")!;
  g.imageSmoothingEnabled = false;
  g.clearRect(0, 0, out.width, out.height);
  g.drawImage(source, pad, pad);

  // Only paint onto existing opaque dog pixels - never fill the billboard quad.
  g.globalCompositeOperation = "source-atop";

  g.fillStyle = "rgba(255, 214, 196, 0.16)";
  g.fillRect(0, 0, out.width, out.height);
  g.fillStyle = "rgba(255, 150, 185, 0.1)";
  g.fillRect(0, 0, out.width, out.height);

  const cheekY = pad + source.height * 0.54;
  const cheekR = Math.max(4, source.width * 0.1);
  g.fillStyle = "rgba(255, 110, 150, 0.42)";
  g.beginPath();
  g.ellipse(pad + source.width * 0.48, cheekY, cheekR, cheekR * 0.68, 0, 0, Math.PI * 2);
  g.fill();
  g.beginPath();
  g.ellipse(pad + source.width * 0.78, cheekY, cheekR * 0.95, cheekR * 0.62, 0, 0, Math.PI * 2);
  g.fill();

  g.fillStyle = "rgba(255, 255, 255, 0.9)";
  for (const [sx, sy, sr] of [
    [0.69, 0.32, 0.04],
    [0.76, 0.28, 0.026],
    [0.64, 0.28, 0.018],
  ] as const) {
    g.beginPath();
    g.arc(pad + source.width * sx, pad + source.height * sy, Math.max(1.2, source.width * sr), 0, Math.PI * 2);
    g.fill();
  }

  g.globalCompositeOperation = "source-over";
  // Bake a 1px cream rim into the sprite - thin outer stroke only.
  return paintPixelOutline(out, [255, 248, 231, 255], 1);
}

/** Dilate alpha into transparent neighbors, then redraw the sprite on top. */
function paintPixelOutline(
  source: HTMLCanvasElement,
  rgb: [number, number, number, number],
  radius: number,
): HTMLCanvasElement {
  const w = source.width;
  const h = source.height;
  const ctx = source.getContext("2d")!;
  const src = ctx.getImageData(0, 0, w, h).data;
  const out = ctx.createImageData(w, h);
  const dst = out.data;
  const isSolid = (x: number, y: number): boolean => {
    if (x < 0 || y < 0 || x >= w || y >= h) {
      return false;
    }
    return src[(y * w + x) * 4 + 3]! > 40;
  };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (src[i + 3]! > 40) {
        dst[i] = src[i]!;
        dst[i + 1] = src[i + 1]!;
        dst[i + 2] = src[i + 2]!;
        dst[i + 3] = src[i + 3]!;
        continue;
      }
      let ring = false;
      for (let oy = -radius; oy <= radius && !ring; oy++) {
        for (let ox = -radius; ox <= radius; ox++) {
          if (ox * ox + oy * oy > radius * radius + radius) {
            continue;
          }
          if (isSolid(x + ox, y + oy)) {
            ring = true;
            break;
          }
        }
      }
      if (ring) {
        dst[i] = rgb[0];
        dst[i + 1] = rgb[1];
        dst[i + 2] = rgb[2];
        dst[i + 3] = rgb[3];
      }
    }
  }

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d")!.putImageData(out, 0, 0);
  return canvas;
}

function drawFallbackDog(g: CanvasRenderingContext2D, breed: DogBreedDef): void {
  g.fillStyle = "#f0c090";
  ellipse(g, 50, 58, 34, 20);
  ellipse(g, 78, 42, 22, 20);
  g.fillStyle = "rgba(255, 120, 160, 0.65)";
  ellipse(g, 68, 50, 8, 5);
  ellipse(g, 90, 50, 7, 4);
  g.fillStyle = "#5d4037";
  ellipse(g, 78, 42, 3.5, 2.5);
  void breed;
}

function ellipse(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
  rot = 0,
): void {
  g.beginPath();
  g.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2);
  g.fill();
}

function roundRect(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  g.beginPath();
  g.roundRect(x, y, w, h, r);
  g.fill();
}

function cssColor(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

function darken(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const k = 1 - amount;
  return ((r * k) << 16) | ((g * k) << 8) | (b * k);
}
